(ns hai.home.desktop.mini-apps.clock
  "Clock mini-app card: today's date in the header band, the current time, and
   the signed-in account underneath with a small \"signed in\" dot. The address
   hangs off the bottom rather than joining the time, and hovering it gives the
   full text when it doesn't fit."
  (:require
   [hai.datetime :as datetime]
   [hai.home.clock :as clock]
   [hai.mini-apps.data :as mini-apps-data]
   [hai.mini-apps.frame :as mini-apps]
   [hai.ui.components.tooltip :as tt]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; Card frame
;; ---------------------------------------------------------------------------

;; Garden keyframes are evaluated at macro time, so garden.stylesheet needs no
;; cljs require here.
#_{:clj-kondo/ignore [:clojure-lsp/unused-public-var]}
(o/defrules separator-blink
  (garden.stylesheet/at-keyframes
   :clock-separator-blink
   [:from {:opacity "1"}]
   ["50%" {:opacity "0"}]
   [:to {:opacity "1"}]))

(o/defstyled card mini-apps/card
  {:min-width "380px"}
  [:&::before {:left "24px"}]
  ;; Header: the same system gray band as Tips and tricks — this is Humble's
  ;; own card, not one of the user's apps.
  [:.app-header {:background t/--system}]
  ;; :access_time is a Material fill icon, unlike the stroke icons the shared
  ;; card frame assumes, so restore fill and match the other header icons' size.
  [:.app-icon [:svg {:fill "currentColor" :width "20px" :height "20px"}]]
  [:.app-title {:white-space "nowrap"}]
  ;; The header never wraps: below the page's narrow breakpoint the date drops
  ;; to its short form instead of ellipsizing.
  [:.date-short {:display "none"}]
  [:at-media {:max-width "768px"}
   [:.date-long {:display "none"}]
   [:.date-short {:display "inline"}]]
  [:.clock-body
   {:padding "22px 20px 20px"
    :display "flex" :flex-direction "column" :gap "14px"}]
  [:.time {:display "flex" :align-items "baseline" :gap "8px"}]
  [:.hours-minutes
   {:font-size "52px" :font-weight "800" :color t/--card-forest
    :line-height "0.9" :letter-spacing "-0.02em"}]
  ;; A slow blink: a second on, a second off. The one-per-second tick of a
  ;; real clock is too busy for a card you glance at.
  [:.separator {:animation "clock-separator-blink 2s step-end infinite"}]
  [:.day-period {:font-size "19px" :font-weight "700" :color t/--link-muted}]
  ;; Zero width plus a 100% minimum keeps a long address from widening the card:
  ;; the card measures itself as if this row were empty, then the row fills
  ;; whatever width the header and time settled on and clips to one line.
  [:.account
   {:display "flex" :align-items "center" :gap "8px"
    :width "0" :min-width "100%"}]
  [:.status-dot
   {:width "6px" :height "6px" :flex "none" :border-radius "50%"
    :background t/--status-online}]
  ;; The address links to the profile page, quietly: underlined only on hover,
  ;; like the card title links.
  [:.account-email
   {:font-size "13px" :color t/--link-muted
    :min-width "0"
    :white-space "nowrap" :overflow "hidden" :text-overflow "ellipsis"
    :text-decoration "none"}
   [:&:hover {:text-decoration "underline" :text-underline-offset "3px"}]])

;; ---------------------------------------------------------------------------
;; Panel
;; ---------------------------------------------------------------------------

(defn- account-email
  "The signed-in address on one clipped line, linking to the profile page,
   wrapped in a tooltip only while it is too wide to render in full."
  [address]
  (r/with-let [clipped? (r/atom false)
               email-line (r/atom nil)
               measure! (fn []
                          (when-let [element @email-line]
                            (reset! clipped?
                                    (> (.-scrollWidth element) (.-clientWidth element)))))
               _ (js/window.addEventListener "resize" measure!)]
    ;; A plain :a is the trigger — Ornament doesn't forward refs, which
    ;; Radix's trigger composition needs.
    (let [line [:a.account-email
                {:href (routing/href :admin-ui/profile)
                 :target routing/link-target
                 :ref (fn [element]
                        (reset! email-line element)
                        (measure!))}
                address]]
      (if @clipped?
        [tt/tooltip {:content address} line]
        line))
    (finally (js/window.removeEventListener "resize" measure!))))

(defn panel [_mini-app]
  (let [{:keys [hour minute day-period]} @(<s [::clock/parts])
        account @(<s :auth/identity)
        address (first (:user/email account))
        collapsed? @(<s [::mini-apps-data/collapsed? :clock])]
    [card {:class (when collapsed? "collapsed")}
     [:div.app-header
      [:span.app-icon {:aria-hidden "true"} [icons/outlined-access_time]]
      [:h2.app-title
       [:span.date-long @(<s [::clock/date-label])]
       [:span.date-short @(<s [::clock/date-label-short])]]
      [:div.app-actions
       [mini-apps/collapse-toggle
        {:collapsed? collapsed?
         :label      "clock"
         :class      "app-action-btn"
         :on-toggle  #(>e [::mini-apps-data/toggle-collapsed :clock])}]]]
     (when-not collapsed?
       [:div.clock-body
        [:div.time
         [:span.hours-minutes
          [:span.hour hour]
          [:span.separator datetime/time-separator]
          [:span.minute minute]]
         (when day-period
           [:span.day-period day-period])]
        (when address
          [:div.account
           [:span.status-dot {:aria-hidden "true"}]
           [account-email address]])])]))
